import { createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { Grouping } from './models';
import { baseApi } from '@centerpiece/components';
import { project } from './project.api';

export const grouping = baseApi
  .enhanceEndpoints({ addTagTypes: ['Groupings', 'Grouping', 'Project'] })
  .injectEndpoints({
    endpoints: builder => ({
      getGroupings: builder.query<Grouping[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: 'grouping',
          method: 'GET',
          params: params ? params : { nested: 'all' },
        }),
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({ type: 'Grouping' as const, id })),
                'Groupings',
              ]
            : ['Groupings'],
      }),
      getGrouping: builder.query<Grouping, { id: number }>({
        query: ({ id }: { id: number }) => ({
          url: `grouping/${id}`,
          method: 'GET',
        }),
        providesTags: (result, error, arg) => [
          { type: 'Grouping', id: arg.id },
        ],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
      }),
      createGrouping: builder.mutation({
        query: request => ({
          url: 'grouping',
          method: 'POST',
          body: request,
        }),
        // invalidatesTags: ['Groupings'],
        invalidatesTags: (result, error, arg) => [
          'Groupings',
          { type: 'Project', id: arg.project },
        ],
      }),
      patchGrouping: builder.mutation<Grouping, Grouping>({
        query: request => ({
          url: `grouping/${request.id}`,
          method: 'PATCH',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'Grouping', id: arg.id },
          { type: 'Project', id: arg.project },
        ],
        async onQueryStarted(
          { id, project: pid, sequence = 0, ...patch },
          { dispatch, queryFulfilled }
        ) {
          const patchResults = dispatch(
            project.util.updateQueryData(
              'getProject',
              { id: pid, params: { optimized: true } },
              draftProject => {
                //Find task and update
                const newProject = {
                  ...draftProject,
                  groupings: [
                    ...draftProject?.groupings?.map(grouping => {
                      if (grouping.id === id) {
                        return {
                          ...grouping,
                          ...patch,
                          // sequence: grouping.sequence > sequence ? grouping.sequence + 1 : grouping.sequence,
                        };
                      } else {
                        return {
                          ...grouping,
                          // sequence: grouping.sequence > sequence ? grouping.sequence + 1 : grouping.sequence,
                        };
                      }
                    }),
                  ],
                };
                Object.assign(draftProject, newProject);
              }
            )
          );
          try {
            await queryFulfilled;
          } catch {
            patchResults.undo();
            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(task.util.invalidateTags(['Tasks']))
             */
            dispatch(
              project.util.invalidateTags([{ type: 'Project', id: pid }])
            );
          }
        },
      }),
      updateGrouping: builder.mutation<Grouping, Grouping>({
        query: request => ({
          url: `grouping/${request.id}`,
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'Grouping', id: arg.id },
          { type: 'Project', id: arg.project },
        ],
        async onQueryStarted(
          { id, project: pid, sequence = 0, ...patch },
          { dispatch, queryFulfilled }
        ) {
          const patchResults = dispatch(
            project.util.updateQueryData(
              'getProject',
              { id: pid, params: { optimized: true } },
              draftProject => {
                //Find task and update
                const newProject = {
                  ...draftProject,
                  groupings: [
                    ...draftProject?.groupings?.map(grouping => {
                      if (grouping.id === id) {
                        return {
                          ...grouping,
                          ...patch,
                          sequence:
                            grouping.sequence > sequence
                              ? grouping.sequence + 1
                              : grouping.sequence,
                        };
                      } else {
                        return {
                          ...grouping,
                          sequence:
                            grouping.sequence > sequence
                              ? grouping.sequence + 1
                              : grouping.sequence,
                        };
                      }
                    }),
                  ],
                };
                Object.assign(draftProject, newProject);
              }
            )
          );
          try {
            await queryFulfilled;
          } catch {
            patchResults.undo();
            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(task.util.invalidateTags(['Tasks']))
             */
            dispatch(
              project.util.invalidateTags([{ type: 'Project', id: pid }])
            );
          }
        },
      }),
      deleteGrouping: builder.mutation<Grouping, number>({
        query: id => ({
          url: `grouping/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result
            ? [{ type: 'Grouping' as const, arg }, 'Groupings', 'Project']
            : ['Groupings'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  usePatchGroupingMutation,
  useGetGroupingsQuery,
  useGetGroupingQuery,
  useCreateGroupingMutation,
  useUpdateGroupingMutation,
  useDeleteGroupingMutation,
  useLazyGetGroupingQuery,
  usePrefetch: useGroupingsPrefetch,
} = grouping;
const selectGroupingsRoot = grouping.endpoints.getGroupings.select({});
export const selectGroupings = createSelector(
  selectGroupingsRoot,
  grouping => grouping.data || []
);

export const subscribeGrouping = grouping.endpoints.getGrouping.useQueryState;
